import React, { useState, useEffect } from 'react';
import { Star, Quote, ExternalLink, MapPin, Loader2 } from 'lucide-react';

// Format name as "First L." for privacy
const formatName = (fullName) => {
  if (!fullName) return 'Anonymous';
  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return parts[0];
  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${firstName} ${lastInitial}.`;
};

const GoogleReviews = () => {
  const [reviewsData, setReviewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fallback reviews: only verified real client reviews are shown as text.
  // The aggregate rating/total reflects Amy's actual Google Business Profile
  // (5.0 / 23 reviews). Do NOT add fabricated testimonials — attorney
  // advertising rules (Washington RPC 7.1) prohibit false or misleading statements.
  const fallbackReviews = {
    place_name: "Amy C Brown Law",
    rating: 5.0,
    total_reviews: 23,
    reviews: [
      {
        author_name: "John H",
        rating: 5,
        text: "Amy helped me with my auto accident. She was very professional and got me the best outcome that could have happened in this situation. I highly recommend her for auto/injury litigation.",
        relative_time_description: "2 months ago"
      }
    ],
    place_url: "https://www.google.com/maps/place/?q=place_id:ChIJz_RHxuirmlQR939RFXobg2o"
  };

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${apiUrl}/api/google-reviews`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch reviews');
        }
        
        const data = await response.json();
        setReviewsData(data);
      } catch (err) {
        console.error('Error fetching Google reviews:', err);
        setError(err.message);
        // Use fallback data on error
        setReviewsData(fallbackReviews);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <Star 
        key={index}
        className={`w-5 h-5 ${index < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  // Use fallback while loading or if no data
  const data = reviewsData || fallbackReviews;
  const reviews = data.reviews || [];
  const averageRating = data.rating || 5.0;
  const totalReviews = data.total_reviews || reviews.length;
  const placeUrl = data.place_url || "https://www.google.com/maps/place/?q=place_id:ChIJz_RHxuirmlQR939RFXobg2o";

  return (
    <section className="py-20 bg-white" data-testid="google-reviews-section" aria-labelledby="reviews-heading">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <svg className="w-8 h-8" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-lg font-medium text-slate-600">Google Reviews</span>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-teal-600" />}
          </div>
          <h2 id="reviews-heading" className="font-heading text-3xl md:text-4xl font-bold text-secondary mb-4">
            What Clients Say About Working With Me
          </h2>
          
          {/* Overall Rating */}
          <div className="flex items-center justify-center gap-4 mb-4" role="img" aria-label={`Rated ${averageRating.toFixed(1)} out of 5 stars based on ${totalReviews} reviews`}>
            <div className="flex items-center gap-1" aria-hidden="true">
              {renderStars(Math.round(averageRating))}
            </div>
            <span className="text-2xl font-bold text-secondary">{averageRating.toFixed(1)}</span>
            <span className="text-slate-500">({totalReviews} reviews)</span>
          </div>
        </div>

        {/* Reviews Grid - centered single card when only one review present */}
        <div className={`${reviews.length === 1 ? 'max-w-2xl mx-auto' : 'grid md:grid-cols-2 gap-6 max-w-5xl mx-auto'}`}>
          {reviews.slice(0, 4).map((review, index) => (
            <div 
              key={index}
              className="bg-slate-50 rounded-2xl p-6 hover-lift border border-slate-100"
              data-testid={`review-card-${index}`}
            >
              <div className="flex items-start gap-4 mb-4">
                {review.profile_photo_url ? (
                  <img 
                    src={review.profile_photo_url} 
                    alt={formatName(review.author_name)}
                    loading="lazy"
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {review.author_name.charAt(0)}
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="font-semibold text-secondary">{formatName(review.author_name)}</h4>
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {renderStars(review.rating)}
                    </div>
                    <span className="text-sm text-slate-400">{review.relative_time_description}</span>
                  </div>
                </div>
                <Quote className="w-8 h-8 text-teal-200 flex-shrink-0" />
              </div>
              <p className="text-slate-600 leading-relaxed">
                "{review.text}"
              </p>
            </div>
          ))}
        </div>

        {/* Helpful note when displayed reviews count is less than total (e.g., during API outage) */}
        {reviews.length < totalReviews && (
          <p className="text-center text-slate-500 text-sm mt-6 max-w-2xl mx-auto" data-testid="reviews-helper-text">
            Showing 1 highlighted review. Read all {totalReviews} of my 5-star Google reviews below.
          </p>
        )}

        {/* Google Business Link */}
        <div className="text-center mt-12">
          <a 
            href={placeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white border-2 border-slate-200 text-slate-700 px-6 py-3 rounded-full font-medium hover:border-teal-500 hover:text-teal-600 transition-all"
            data-testid="google-reviews-link"
          >
            <MapPin className="w-5 h-5" />
            View All Reviews on Google
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
